import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/services/adminApi';

/**
 * Admin User 타입
 * Admin User type
 */
export interface AdminUser {
  id: string;
  username: string;
  role: 'SUPER' | 'NORMAL';
  name?: string;
  email?: string;
}

/**
 * Admin Auth 훅
 * Admin authentication hook
 */
export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasInitializedRef = useRef(false);

  // 초기 인증 상태 확인 (한 번만 실행)
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initAuth = async () => {
      const token = localStorage.getItem('adminAuthToken');
      const userInfo = localStorage.getItem('adminUserInfo');
      
      if (token && userInfo) {
        try {
          const parsedUser = JSON.parse(userInfo);
          setIsAuthenticated(true);
          setUser(parsedUser);
        } catch (e) {
          console.error('Failed to parse admin user info:', e);
          localStorage.removeItem('adminAuthToken');
          localStorage.removeItem('adminUserInfo');
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      
      setIsInitializing(false);
    };

    initAuth();
  }, []);

  // 현재 사용자 정보 조회 (인증된 경우에만 활성화)
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError, error: profileError } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => {
      const response = await adminApi.getMe();
      return response?.data || response;
    },
    enabled: isAuthenticated && !isInitializing,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // 마운트 시 프로필 로딩 보장
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
  });

  // 프로필이 로드되면 사용자 정보 업데이트
  useEffect(() => {
    if (profile) {
      setUser(profile);
      localStorage.setItem('adminUserInfo', JSON.stringify(profile));
    }
  }, [profile]);

  // 프로필 에러 시 인증 상태 초기화
  useEffect(() => {
    if (isProfileError && isAuthenticated && profileError) {
      const error: any = profileError;
      if (error?.response?.status === 401) {
        console.log('Profile fetch returned 401, clearing auth state');
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('adminUserInfo');
        setIsAuthenticated(false);
        setUser(null);
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/admin/login')) {
          navigate('/admin/login', { replace: true });
        }
      }
    }
  }, [isProfileError, isAuthenticated, profileError, navigate]);

  // API 401 에러 이벤트 리스너 (다른 API 호출 실패 시 처리)
  const isHandling401Ref = useRef(false);
  
  useEffect(() => {
    const handleAuth401 = async (event: Event) => {
      // 중복 처리 방지
      if (isHandling401Ref.current || !isAuthenticated) return;
      
      // CustomEvent 타입 체크
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail || {};
      const currentPath = detail.path || window.location.pathname;
      const requestUrl = detail.error?.config?.url || '';
      
      // 로그인 페이지나 로그인 API에서는 처리하지 않음
      if (currentPath.includes('/admin/login') || requestUrl.includes('/api/admin/auth/login')) {
        return;
      }
      
      isHandling401Ref.current = true;
      
      try {
        // 토큰이 없으면 이미 로그아웃된 상태
        const token = localStorage.getItem('adminAuthToken');
        if (!token) {
          setIsAuthenticated(false);
          setUser(null);
          isHandling401Ref.current = false;
          return;
        }
        
        // /api/admin/auth/me 호출로 실제 인증 상태 확인
        await adminApi.getMe();
        // getMe 성공하면 인증은 유효함 (권한 문제일 수 있음)
        isHandling401Ref.current = false;
      } catch (error: any) {
        // getMe도 401이면 실제로 인증 실패
        if (error.response?.status === 401) {
          console.log('getMe also returned 401, logging out');
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('adminAuthToken');
          localStorage.removeItem('adminUserInfo');
          queryClient.clear();
          if (!currentPath.includes('/admin/login')) {
            navigate('/admin/login', { replace: true });
          }
        }
        isHandling401Ref.current = false;
      }
    };

    window.addEventListener('admin-auth-401', handleAuth401);
    return () => {
      window.removeEventListener('admin-auth-401', handleAuth401);
    };
  }, [isAuthenticated, navigate, queryClient]);

  // 로그인 뮤테이션
  const loginMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) => {
      return adminApi.login(username, password);
    },
    onSuccess: (data) => {
      const token = data?.access_token;
      const userData = data?.user;
      
      if (token && userData) {
        localStorage.setItem('adminAuthToken', token);
        localStorage.setItem('adminUserInfo', JSON.stringify(userData));
        setIsAuthenticated(true);
        setUser(userData);
        setIsInitializing(false);
        queryClient.invalidateQueries({ queryKey: ['admin'] });
        // 상태 업데이트 후 자연스럽게 리다이렉트 (useEffect에서 처리)
      } else {
        console.error('Login failed: Missing token or userData', { token, userData });
      }
    },
    onError: (error: any) => {
      console.error('Login error:', error);
    },
  });

  // 로그아웃 함수
  const logout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUserInfo');
    setIsAuthenticated(false);
    setUser(null);
    setIsInitializing(false);
    queryClient.clear();
    navigate('/admin/login', { replace: true });
  };

  // 로그인 함수 (Promise 반환)
  const login = async (username: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      loginMutation.mutate(
        { username, password },
        {
          onSuccess: () => {
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        },
      );
    });
  };

  // 권한 확인
  const isSuper = (profile || user)?.role === 'SUPER';
  const isNormal = (profile || user)?.role === 'NORMAL';

  // 로딩 상태 계산
  const isLoading = isInitializing || loginMutation.isPending || (isAuthenticated && isProfileLoading && !user);

  return {
    isAuthenticated,
    user: profile || user,
    isLoading,
    login,
    logout,
    loginError: loginMutation.error,
    isSuper,
    isNormal,
  };
};
